// janus-engine/ability/paoe-entry.ts — the browser PERSISTENT-AOE ENTRY runner (MV2 ASK-2; built by MV4, owner-authorized 2026-09-28).
//
// PHP: janus_try_persistent_aoe_entry_for_creature() (janus/core/janus_try_persistent_aoe_entry_for_creature.php:259-281) calls
//   janus(item_id_used: <zone origin>, caster_arr: <zone caster>, targets_arr: [<entering creature>], use_ability_variable_1: 'moving_into_p_aoe',
//         status_connected_id: <the zone's connection>, status_casting_targets_arr: <entering id>, roll_private: 0, raw dice 0)
// This runner is that janus() call — janus.ts janusPaoeEntry() holds the frame's rules. The ORCHESTRATION around it (per-move dedupe, the
// team gate, trap / prismatic / painted zones, halts, the forced-extreme snapshot) is the walk's (zones/, websocket/zone-entry-truth.ts).
// Its verdict is the ability lane's own `resolved` shape: it is persisted by the SHARED commit (ability_truth_commit_inc.php `zone_entry`,
// owner 2026-09-29) — rows, columns, statuses, removals, stacks, charges, PC columns and the actual-outcome typed death plan.
//
// Oracle: tools/test/fixtures/janus-ability-paoe-entry-oracle.ndjson (tools/probes/janus_ability_paoe_entry_cell.php) →
// tools/test/janus-ability-paoe-entry.test.mjs: every case EXACT or handed back, never WRONG.

import { type JanusCtx, type Row, HandBack, ecId, record, upper, definitionNotHeld } from './context.js';
import { janus } from './janus.js';
import { PAOE_ENTRY } from './paoe-entry-mode.js';
import type { AbilityUseVerdict } from '../actions/ability-use.js';
import { resolvedVerdict } from './resolved-verdict.js';
import { frameCopyOnRead, type FrameCopy } from './frame-snapshot.js';
import { decisionWrites, movementDecisionView } from '../movement/decision-view.js';

export interface PaoeEntryStep {
  /** The zone's origin (status_origin_id ?? status_item_id), resolved to a definition by the caller. */
  abilityId: string;
  /** The zone row's status_caster_id (a live member). */
  casterId: string;
  /** The creature that stepped in. */
  enteringId: string;
  /** The zone's status_connected_id — every status the entry applies rides it (concentration / exit cleanup). */
  statusConnectedId: string;
  forcedExtreme?: number;
}

/** The zone's cast-time extreme applies to its caster for this entry alone, for walks and endpoint relocations alike. */
export function paoeEntryReaders(base: JanusCtx['read'], step: PaoeEntryStep): JanusCtx['read'] {
  if (!step.forcedExtreme) return base;
  const damage = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0', dot = '77A044B7-7041-CA96-3C63-BB718B15A364';
  const definition = base.definition(step.abilityId);
  const tick = upper(definition ? base.value(definition, 'roll_type') : '') === dot ? dot : damage;
  const side = step.forcedExtreme > 0 ? 'roll_type_is_rolled_at_max' : 'roll_type_is_rolled_at_min';
  const idOf = (entry: unknown): string => upper(typeof entry === 'string' ? entry.split('|')[0] : Array.isArray(entry) ? entry[0]
    : record(entry)?.item_id ?? record(record(entry)?.items)?.item_id);
  return { ...base, entries: (row, key) => {
    const list = base.entries(row, key);
    if (!row || ecId(row) !== upper(step.casterId) || (key !== 'roll_type_is_rolled_at_max' && key !== 'roll_type_is_rolled_at_min')) return list;
    const kept = list.filter(entry => idOf(entry) !== tick);
    return key === side ? [...kept, tick] : kept;
  } };
}

export type PaoeEntryVerdict = Extract<AbilityUseVerdict, { kind: 'resolved' }> & { statusesRemoved: JanusCtx['statusesRemoved'] };

/** A fresh ctx over `members` (a walk's second entry sees the first one's state).
 * Ordinary rows/stacks persist in the shared commit; unresolved corpse and inventory plans refuse. */
export function paoeEntryContext(encounter: Row, members: ReadonlyMap<string, Row>, read: JanusCtx['read'], roll: JanusCtx['roll'], synchronousCopy?: () => FrameCopy): JanusCtx {
  // The factory isolates working writes; held inputs remain the stable before-view for this synchronous run.
  const held = synchronousCopy ? { ...synchronousCopy(), originals: new Map([...members].map(([id, member]) => [upper(id), member])) } : movementDecisionView(encounter, members);
  return {
    ...held,
    read, roll, rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [], statusConnectedId: null,
    rollPrivate: 0, deathTail: null, concentrationBreaks: null, statusStacks: [],
  };
}

/** Apply an entry on the caller's held frame. Surface occupants share their action's writes and roll source. */
export function applyPaoeEntry(ctx: JanusCtx, step: PaoeEntryStep): void {
  const inventoryBefore = ctx.inventoryChanged.length;
  const definition = ctx.read.definition(step.abilityId);
  if (!definition) definitionNotHeld('paoe-entry:definition-not-held', step.abilityId);
  const caster = ctx.members.get(upper(step.casterId));
  const target = ctx.members.get(upper(step.enteringId));
  if (!caster) throw new HandBack('paoe-entry:caster-not-held');
  if (!target) throw new HandBack('paoe-entry:entering-not-held');
  if (!/^[0-9A-F-]{36}$/i.test(step.statusConnectedId)) throw new HandBack('paoe-entry:connection');
  ctx.statusConnectedId = upper(step.statusConnectedId);
  const base = ctx.read;
  ctx.read = paoeEntryReaders(base, step);
  try {
    janus(ctx, {
      casterId: upper(step.casterId), targetIds: [upper(step.enteringId)], abilityId: upper(step.abilityId),
      rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: PAOE_ENTRY, useAbilityVariable2: null, castingTargets: upper(step.enteringId),
    }, frameCopyOnRead(definition!, new Map(), { catalogs: 'share' }).encounter, caster, [target]);
  } finally { ctx.read = base; }
  if (ctx.inventoryChanged.length > inventoryBefore && !ctx.saveBoundaryInventory && !ctx.surfaceReactionFrame) throw new HandBack('paoe-entry:inventory');
}

/** Standalone walk/relocation callers serialize once after their entry. */
export function runPaoeEntry(ctx: JanusCtx, step: PaoeEntryStep): PaoeEntryVerdict {
  applyPaoeEntry(ctx, step);
  return paoeEntryVerdict(ctx);
}

/** Shared ability-verdict shape for persistent-zone entry and exact exit frames. */
export function paoeEntryVerdict(ctx: JanusCtx): PaoeEntryVerdict {
  return decisionWrites({ ...resolvedVerdict(ctx), statusesRemoved: ctx.statusesRemoved });
}
